import { useTranslation } from "@/hooks";
import {
	formatCurrency,
	formatLargeNumber,
	formatNumber,
	formatPrice,
} from "@/utils/currency";
import type { CurrencyFormatOptions } from "@/utils/currency";

/**
 * Hook for currency formatting with translation support
 * @returns Currency formatting functions
 */
export function useCurrencyFormat() {
	const { lang } = useTranslation();

	return {
		/**
		 * Format number with current language settings
		 */
		formatNumber: (value: number | string) => {
			return formatNumber(value, lang);
		},

		/**
		 * Format currency with current language settings
		 */
		formatCurrency: (
			value: number | string,
			options?: Omit<CurrencyFormatOptions, "language">,
		) => {
			return formatCurrency(value, { ...options, language: lang });
		},

		/**
		 * Format price for table display (with k suffix)
		 */
		formatPrice: (value: number | string) => {
			return formatPrice(value, lang);
		},

		/**
		 * Format large numbers (millions)
		 */
		formatLargeNumber: (value: number | string) => {
			return formatLargeNumber(value, lang);
		},

		/**
		 * Format with custom options
		 */
		format: (
			value: number | string,
			options?: Omit<CurrencyFormatOptions, "language">,
		) => {
			return formatCurrency(value, { ...options, language: lang });
		},
	};
}
